import { useMutation } from '@tanstack/react-query';

import type { AddressValidation } from '../api/types';
import { MutationError } from '../components/MutationError';
import { useProjectApi } from '../projects/useProjectApi';
import { GoogleAttribution } from './GoogleAttribution';

type Address = { street: string; house_number: string; postal_code: string; city: string };

const fieldLabels: Record<string, string> = { street: 'Straße', house_number: 'Hausnr.', postal_code: 'PLZ', city: 'Ort' };

// AddressCheck: Adresse bei Google prüfen (Address Validation API). Korrekturen übernimmt der Mitarbeiter per Klick.
// country: Land der Anschrift (Standard Deutschland; nicht jedes Land prüft Google).
export function AddressCheck({ address, onApply, country = 'DE' }: { address: Address; onApply: (a: Address) => void; country?: string }) {
  const papi = useProjectApi();
  const check = useMutation({ mutationFn: () => papi.placesValidate({ ...address, country_code: country }) });
  const v: AddressValidation | undefined = check.data;
  const canCheck = address.street.trim() !== '' && (address.postal_code.trim() !== '' || address.city.trim() !== '');
  const apply = (r: AddressValidation) => {
    onApply({
      street: r.street || address.street,
      house_number: r.house_number || address.house_number,
      postal_code: r.postal_code || address.postal_code,
      city: r.city || address.city,
    });
    check.reset();
  };
  return (
    <div className="address-check">
      <button type="button" className="btn btn-small" disabled={!canCheck || check.isPending} onClick={() => check.mutate()}>
        {check.isPending ? 'Prüft …' : 'Adresse prüfen'}
      </button>
      {check.isError && <MutationError error={check.error} />}
      {v && (
        <div className="address-check-result" role="status">
          {v.status === 'CONFIRMED' && <span className="badge badge-ok">Adresse bestätigt</span>}
          {v.status === 'CORRECTED' && <span className="badge badge-warn">Korrektur vorgeschlagen</span>}
          {v.status === 'UNSURE' && <span className="badge badge-warn">Nur teilweise gefunden – bitte prüfen</span>}
          {v.status === 'NOT_FOUND' && <span className="badge badge-error">Adresse nicht gefunden</span>}{' '}
          {v.formatted_address && <span>{v.formatted_address}</span>}
          {v.changes.length > 0 && (
            <ul className="plain-list">
              {v.changes.map((c) => (
                <li key={c.field}>
                  {fieldLabels[c.field] ?? c.field}: <s>{c.from || '–'}</s> → <strong>{c.to}</strong>
                </li>
              ))}
            </ul>
          )}
          {(v.status === 'CORRECTED' || v.status === 'UNSURE') && v.changes.length > 0 && (
            <button type="button" className="btn btn-small btn-primary" onClick={() => apply(v)}>
              Korrektur übernehmen
            </button>
          )}
          <GoogleAttribution />
        </div>
      )}
    </div>
  );
}
